import { useQuery } from '@tanstack/react-query';
import { adminService } from '@services/adminService';
import {
  Users,
  Building2,
  DollarSign,
  TrendingUp,
  Activity,
  CreditCard,
  AlertCircle,
} from 'lucide-react';

const fmt = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

const StatCard = ({ label, value, sub, icon: Icon, color = 'indigo' }) => {
  const colors = {
    indigo: 'bg-indigo-500/10 text-indigo-400',
    green: 'bg-green-500/10 text-green-400',
    yellow: 'bg-yellow-500/10 text-yellow-400',
    red: 'bg-red-500/10 text-red-400',
    blue: 'bg-blue-500/10 text-blue-400',
  };
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-400 text-sm">{label}</p>
          <p className="text-white text-2xl font-bold mt-1">{value}</p>
          {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

const PLAN_LABELS = { free: 'Free', pro: 'Pro', family: 'Family' };
const PLAN_COLORS = {
  free: 'bg-gray-700 text-gray-300',
  pro: 'bg-indigo-600 text-white',
  family: 'bg-purple-600 text-white',
};
const STATUS_COLORS = {
  active: 'bg-green-500/20 text-green-400',
  trial: 'bg-yellow-500/20 text-yellow-400',
  suspended: 'bg-red-500/20 text-red-400',
  canceled: 'bg-gray-600/40 text-gray-400',
};

const AdminDashboard = () => {
  const { data: dashData, isLoading } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => adminService.getDashboard(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  const { overview = {}, planBreakdown = {}, recentTenants = [] } = dashData || {};

  return (
    <div>
      <h1 className="text-white text-2xl font-bold mb-1">Dashboard</h1>
      <p className="text-gray-400 text-sm mb-6">Visão geral do sistema</p>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Total de tenants"
          value={overview.totalTenants ?? '—'}
          sub={`${overview.activeTenants ?? 0} ativos`}
          icon={Building2}
        />
        <StatCard
          label="Total de usuários"
          value={overview.totalUsers ?? '—'}
          icon={Users}
          color="blue"
        />
        <StatCard
          label="MRR"
          value={fmt(overview.mrr)}
          sub={`ARR: ${fmt(overview.arr)}`}
          icon={DollarSign}
          color="green"
        />
        <StatCard
          label="Transações / mês"
          value={(overview.totalTransactionsThisMonth ?? 0).toLocaleString('pt-BR')}
          icon={Activity}
          color="yellow"
        />
      </div>

      {/* Plans + recent tenants */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Plan breakdown */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-indigo-400" />
            Distribuição de planos
          </h2>
          <div className="space-y-3">
            {Object.entries(planBreakdown).map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${PLAN_COLORS[plan] || 'bg-gray-700 text-gray-300'}`}>
                  {PLAN_LABELS[plan] || plan}
                </span>
                <div className="flex items-center gap-2">
                  <div
                    className="h-2 bg-indigo-600 rounded-full"
                    style={{
                      width: `${Math.round((count / (overview.totalTenants || 1)) * 100)}px`,
                      minWidth: 4,
                    }}
                  />
                  <span className="text-gray-300 text-sm font-medium">{count}</span>
                </div>
              </div>
            ))}
          </div>
          {overview.suspendedTenants > 0 && (
            <div className="mt-4 flex items-center gap-2 text-red-400 text-xs">
              <AlertCircle className="h-3.5 w-3.5" />
              {overview.suspendedTenants} tenant(s) suspenso(s)
            </div>
          )}
        </div>

        {/* Recent tenants */}
        <div className="lg:col-span-2 bg-gray-900 border border-gray-800 rounded-xl p-5">
          <h2 className="text-white font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-indigo-400" />
            Tenants recentes
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 border-b border-gray-800">
                  <th className="pb-2 text-left font-medium">Nome</th>
                  <th className="pb-2 text-left font-medium">Plano</th>
                  <th className="pb-2 text-left font-medium">Status</th>
                  <th className="pb-2 text-left font-medium">Criado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {recentTenants.map((t) => (
                  <tr key={t.tenantId} className="text-gray-300">
                    <td className="py-2.5 font-medium text-white">{t.name}</td>
                    <td className="py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PLAN_COLORS[t.plan] || 'bg-gray-700 text-gray-300'}`}>
                        {PLAN_LABELS[t.plan] || t.plan}
                      </span>
                    </td>
                    <td className="py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[t.planStatus] || ''}`}>
                        {t.planStatus}
                      </span>
                    </td>
                    <td className="py-2.5 text-gray-500 text-xs">
                      {t.createdAt ? new Date(t.createdAt).toLocaleDateString('pt-BR') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
